import React from 'react'
import { Link } from 'react-router-dom'
import { Product, TYPE_LABELS, CATEGORY_LABELS, MASKED_PRICE } from '../types'
import { formatToman, toFaDigits } from '../utils/format'
import { useCart } from '../context/CartContext'
import { useTrading } from '../context/TradingContext'
import toast from 'react-hot-toast'

/**
 * کارت محصول
 * هر محصول دو دکمه دارد: «سفارش خرید» (ما به مشتری می‌فروشیم)
 * و «سفارش فروش» (ما از مشتری می‌خریم، با درصد منفی خرید).
 */
const ProductCard: React.FC<{ product: Product }> = ({ product }) => {
  const { addItem } = useCart()
  const { buyBlocked, sellBlocked, pricesHidden } = useTrading()

  const hidden = pricesHidden || product.priceHidden || product.sellingPrice === null

  const add = (mode: 'BUY' | 'SELL') => {
    if (mode === 'BUY' && buyBlocked) {
      toast.error('معاملات فروش موقتاً متوقف شده است')
      return
    }
    if (mode === 'SELL' && sellBlocked) {
      toast.error('خرید از مشتریان موقتاً متوقف شده است')
      return
    }
    addItem(product, 1, mode)
  }

  return (
    <div className="card group flex flex-col p-4 !hover:shadow-lg">
      <Link to={`/products/${product.slug}`} className="block">
        <div className="flex items-start justify-between mb-3">
          <span className={`badge ${product.type === 'GOLD' ? 'badge-gold' : 'badge-silver'}`}>
            {TYPE_LABELS[product.type]}
          </span>
          <span className="badge bg-primary-50 text-primary-700">{CATEGORY_LABELS[product.category]}</span>
        </div>

        <h3 className="font-bold text-gray-900 group-hover:text-primary-600 transition-colors leading-6">
          {product.name}
        </h3>

        <div className="flex items-center gap-4 text-xs text-gray-500 mt-2 mb-4">
          <span>وزن: {toFaDigits(product.weight)} گرم</span>
          <span>عیار: {toFaDigits(product.purity)}</span>
        </div>

        <div className="border-t border-gold-200/60 pt-3 space-y-1.5">
          <div className="flex flex-wrap items-center justify-between gap-x-2">
            <span className="text-xs text-stone-500 shrink-0">سفارش خرید (از ما)</span>
            <span className="font-black text-primary-600 price-nums text-sm sm:text-base">
              {hidden ? MASKED_PRICE : formatToman(product.sellingPrice!)}
            </span>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-x-2">
            <span className="text-xs text-stone-500 shrink-0">سفارش فروش (به ما)</span>
            <span className="text-sm font-semibold text-stone-700 price-nums">
              {hidden ? MASKED_PRICE : formatToman(product.buybackPrice!)}
            </span>
          </div>
        </div>
      </Link>

      {/* دو دکمه هر محصول: سفارش خرید و سفارش فروش */}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <button
          className={`btn !py-2 !text-xs ${buyBlocked ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'btn-primary'}`}
          onClick={() => add('BUY')}
          disabled={buyBlocked}
          title={buyBlocked ? 'معاملات فروش موقتاً متوقف شده است' : 'خرید از ما'}
        >
          {buyBlocked ? 'خرید متوقف' : 'سفارش خرید'}
        </button>
        <button
          className={`btn !py-2 !text-xs border ${
            sellBlocked
              ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
              : 'border-gold-400 text-gold-700 bg-gold-50 hover:bg-gold-100'
          }`}
          onClick={() => add('SELL')}
          disabled={sellBlocked}
          title={sellBlocked ? 'خرید از مشتریان موقتاً متوقف شده است' : 'فروش به ما'}
        >
          {sellBlocked ? 'فروش بسته' : 'سفارش فروش'}
        </button>
      </div>
    </div>
  )
}

export default ProductCard
